"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Toaster } from "sonner";

/**
 * Toaster global de `sonner`, siguiendo el tema de la aplicación.
 *
 * Posición arriba a la derecha (la zona menos invasiva para apps con barra
 * lateral izquierda). `richColors` activa los colores semánticos (verde éxito,
 * rojo error). Duración por defecto 4s — suficiente para leer un "Guardado" o
 * un error breve, configurable por toast con `{ duration }`.
 *
 * Por qué esto no es un `<Toaster>` a secas en el layout: el tema de este
 * producto NO es el del sistema operativo, lo elige el usuario dentro de la
 * app (el skin del admin, el tema del puesto del agente). Así que ni
 * `theme="light"` fijo —que era lo que había, y dejaba las notificaciones
 * blancas sobre el workspace oscuro— ni `theme="system"` sirven.
 *
 * El tema se lee del `<body>`, que es donde ambos shells lo reflejan
 * precisamente para que los portales se enteren; sonner monta por portal, así
 * que estaba en el mismo saco que los modales.
 */
export function AppToaster() {
  const [tema, setTema] = useState<"light" | "dark">("light");
  const pathname = usePathname();

  useEffect(() => {
    const leer = () => {
      const d = document.body.dataset;
      setTema(
        d.agentTheme === "dark" || d.odSkin === "dark" ? "dark" : "light"
      );
    };

    // En un setTimeout y no aquí mismo: un setState síncrono en el cuerpo de un
    // efecto encadena renders (y el linter lo marca).
    const primero = setTimeout(leer, 0);
    // El usuario cambia de tema con la app abierta; hay que enterarse.
    const observador = new MutationObserver(leer);
    observador.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-agent-theme", "data-od-skin"],
    });

    return () => {
      clearTimeout(primero);
      observador.disconnect();
    };
  }, []);

  // ── Uno cada vez, y solo en el puesto del agente ─────────────────────────
  // En el puesto el aviso pasa, no se acumula: entra uno y el anterior se va,
  // así nunca hay dos tarjetas peleándose la esquina mientras el agente habla
  // con un cliente. Puede permitírselo porque ahí SÍ hay red debajo — todo
  // queda archivado en la campana y se puede releer.
  //
  // En el admin y en la plataforma NO se toca: no tienen centro de
  // notificaciones, así que pisar un toast con el siguiente perdería el aviso
  // de verdad. Un formulario que devuelve tres errores tiene que poder
  // enseñar los tres.
  const enElPuesto = pathname?.startsWith("/agent") ?? false;

  return (
    <Toaster
      position="top-right"
      richColors
      closeButton
      visibleToasts={enElPuesto ? 1 : 3}
      duration={enElPuesto ? 2500 : 4000}
      // Debajo de la barra superior, no encima. Los 24px por defecto dejaban
      // el aviso solapando la cabecera —y la campana de la que sale—; 68 son
      // los 56 de `--spacing-topbar` más 12 de aire, así que cuelga justo por
      // debajo. El `right` se repite explícito porque el objeto sustituye al
      // valor por defecto entero, no lo completa.
      offset={enElPuesto ? { top: 68, right: 24 } : undefined}
      theme={tema}
    />
  );
}
